import React, { useContext, useEffect } from 'react'

import { ButtonV2, Card, CardBody, CardFooter, Column, Row, Sheet, Text } from '@vividseats/vivid-ui-kit'
import { lockBody } from '@vividseats/vivid-ui-kit/dist/utils/window'

import filterContext from '@/components/pages/production/components/context'
import { FilterSortOption, FilterSort, SortFilterNames } from '@/components/pages/production/components/context/types'
import ticketDetailsContext from '@/components/pages/production/components/ticket-details/context'
import { useLocalizedCurrencyFeature } from '@/optimizely/features/internationalization/hooks/localized-currency'
import { trackEvent } from '@/utils/analytics/ga360'

import FiltersCurrency from '../../filters-currency'
import FiltersPrice from '../../filters-price'
import FiltersTypes from '../../filters-types'
import FiltersQuantityChips from '../components/filters-quantity-chips'
import FiltersTypeChips from '../components/filters-sort-chips'

import styles from './styles.module.scss'

const filterTypePayloads: Record<FilterSort, { name: SortFilterNames }> = {
    price: { name: SortFilterNames.PRICE },
    priceHigh: { name: SortFilterNames.PRICE_HIGH },
    row: { name: SortFilterNames.ROW },
    section: { name: SortFilterNames.SECTION },
} as const

const filterOptions: FilterSortOption[] = [
    {
        value: 'price',
        label: 'Lowest Price',
        dataTestId: 'sortByLowestPrice',
    },
    {
        value: 'priceHigh',
        label: 'Highest Price',
        dataTestId: 'sortByHighestPrice',
    },
    {
        value: 'row',
        label: 'Sort by Row',
        dataTestId: 'sortByRow',
    },
    {
        value: 'section',
        label: 'Sort by Section',
        dataTestId: 'sortBySection',
    },
]

interface Props {
    showMobileFilters: boolean
    toggleShowMobileFilters: () => void
}

const FiltersMobile: React.FC<Props> = ({ showMobileFilters, toggleShowMobileFilters }) => {
    const {
        selectors: { getCurrentSortByValueSelector, getViewResultsButtonTextSelector },
        dispatch: { onSortFilterChangeAction: onSortFilterChange, onFilterResetAction: onFilterReset },
    } = useContext(filterContext)
    const {
        state: detailsState,
        dispatch: { toggleDetails },
    } = useContext(ticketDetailsContext)

    const [isLocalizedCurrencyFeatureEnabled] = useLocalizedCurrencyFeature()

    useEffect(() => {
        lockBody('listings-mobile-filters', showMobileFilters)
    }, [showMobileFilters])

    return (
        <Sheet toggle={showMobileFilters} className={styles.filtersSheet} enterFrom="bottom">
            <Card className={styles.card}>
                <CardBody>
                    <Column className={styles.cardColumn}>
                        <div className={styles.filterRow}>
                            <Row justify="between" align="baseline" className={styles.fullWidth}>
                                <Text as="h2">Filter Results</Text>
                                <ButtonV2
                                    importance="tertiary"
                                    size="small"
                                    data-testid={'mobile-filters-reset-button'}
                                    className={styles.buttonText}
                                    onClick={() => {
                                        if (detailsState.listing) {
                                            toggleDetails({ listing: null })
                                        }
                                        onFilterReset()
                                        trackEvent('FilterUse', 'ResetTicketFilters', 'Reset', undefined, false)
                                    }}
                                >
                                    <Text as="span">Reset</Text>
                                </ButtonV2>
                            </Row>
                            <div className={styles.divider}></div>
                        </div>

                        {/* Sort by filter */}
                        <div className={styles.filterRow}>
                            <Row className={styles.fullWidth}>
                                <Text weight="bold">Sort By</Text>
                            </Row>
                            <Row className={`${styles.fullWidth}`}>
                                <Column>
                                    <FiltersTypeChips
                                        activeFilter={getCurrentSortByValueSelector}
                                        options={filterOptions}
                                        onClick={(filterType: FilterSort) => {
                                            if (detailsState.listing) {
                                                toggleDetails({ listing: null })
                                            }
                                            onSortFilterChange(filterTypePayloads[filterType])
                                        }}
                                    />
                                </Column>
                            </Row>
                        </div>

                        <div className={styles.divider}></div>

                        {/* Ticket quantity filter */}
                        <div className={styles.filterRow}>
                            <Row className={styles.fullWidth}>
                                <Text weight="bold">Number of tickets</Text>
                            </Row>
                            <Row className={styles.fullWidth}>
                                <FiltersQuantityChips />
                            </Row>
                        </div>

                        <div className={styles.divider}></div>

                        {/* Price slider & input filter */}
                        <div className={styles.filterRow}>
                            <Row className={styles.fullWidth}>
                                <Column>
                                    <Text weight="bold">Price per ticket</Text>
                                </Column>
                            </Row>
                            <Row>
                                <Column className={styles.fullWidth}>
                                    <FiltersPrice />
                                </Column>
                            </Row>
                        </div>

                        <div className={styles.divider}></div>

                        {/* Currency filter */}
                        {isLocalizedCurrencyFeatureEnabled && (
                            <>
                                <div className={styles.filterRow}>
                                    <Row className={styles.fullWidth}>
                                        <Column>
                                            <Text weight="bold">Choose your display currency</Text>
                                        </Column>
                                    </Row>
                                    <Row>
                                        <Column className={styles.fullWidth}>
                                            <FiltersCurrency />
                                        </Column>
                                    </Row>
                                </div>

                                <div className={styles.divider}></div>
                            </>
                        )}

                        {/* Type  of ticket filter */}
                        <div className={styles.filterRow}>
                            <Row className={styles.fullWidth}>
                                <Text weight="bold">Type of tickets</Text>
                            </Row>
                            <Row className={styles.fullWidth}>
                                <FiltersTypes />
                            </Row>
                        </div>
                    </Column>
                </CardBody>

                <CardFooter justify="between" className={styles.footer}>
                    <Row justify="between" align="center" className={styles.buttonRow}>
                        <ButtonV2
                            size="large"
                            importance="tertiary"
                            className={styles.dismissButton}
                            onClick={() => {
                                toggleShowMobileFilters()
                            }}
                            data-testid="mobile-filters-dismiss-button"
                        >
                            <Text as="span" size="md">
                                Dismiss
                            </Text>
                        </ButtonV2>
                        <ButtonV2
                            size="medium"
                            className={styles.vewResultsButton}
                            onClick={() => {
                                toggleShowMobileFilters()
                            }}
                            data-testid="mobile-filters-view-results-button"
                        >
                            {getViewResultsButtonTextSelector}
                        </ButtonV2>
                    </Row>
                </CardFooter>
            </Card>
        </Sheet>
    )
}

export default FiltersMobile
